import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";

import SPECIES from "#/model/SpeciesCodeList.js";
import ApiClient from "#/redux/api/ApiClient";
import { getErrorMessage } from "#/utils/APIUtils";

const baseUrl = "/api/sif/species";

const initialState = {
  list: [],
  isListLoading: false,
  page: {},
  // map by sifra => item
  byId: {},
  details: null,

  isLoading: false,
  error: null,
};

/**
 * GET /api/sif/species
 * Returns full list of species codes.
 */
export const fetchSpeciesCodeList = createAsyncThunk(
  "speciesCodeLists/fetchAll",
  async (_, { rejectWithValue }) => {
    // eslint-disable-next-line no-console
    console.warn("Deprecated, migrate to LookupTable hooks and components");

    try {
      const response = await ApiClient.get(`${baseUrl}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * GET /api/sif/species/{sifra}
 */
export const fetchSpeciesCodeBySifra = createAsyncThunk(
  "speciesCodeLists/fetchBySifra",
  async (sifra, { rejectWithValue }) => {
    // eslint-disable-next-line no-console
    console.warn("Deprecated, migrate to LookupTable hooks and components");

    try {
      const response = await ApiClient.get(`${baseUrl}/${sifra}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * GET /api/sif/species/page
 */
export const fetchSpeciesCodePage = createAsyncThunk(
  "speciesCodeLists/fetchPage",
  async ({ page, filter, sort } = {}, { rejectWithValue }) => {
    // eslint-disable-next-line no-console
    console.warn("Deprecated, migrate to LookupTable hooks and components");

    try {
      const response = await ApiClient.get(
        `${baseUrl}/page`,
        undefined,
        page,
        filter,
        sort,
        true // stripQueryParamsForCancelPrevious
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * POST /api/sif/species
 */
export const createSpeciesCode = createAsyncThunk(
  "speciesCodeLists/create",
  async (payload, { rejectWithValue }) => {
    // eslint-disable-next-line no-console
    console.warn("Deprecated, migrate to LookupTable hooks and components");

    try {
      const response = await ApiClient.post(`${baseUrl}`, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * PUT /api/sif/species/{sifra}
 */
export const updateSpeciesCode = createAsyncThunk(
  "speciesCodeLists/update",
  async ({ sifra, data }, { rejectWithValue }) => {
    // eslint-disable-next-line no-console
    console.warn("Deprecated, migrate to LookupTable hooks and components");

    try {
      const response = await ApiClient.put(`${baseUrl}/${sifra}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/**
 * DELETE /api/sif/species/{sifra}
 */
export const deleteSpeciesCode = createAsyncThunk(
  "speciesCodeLists/delete",
  async (sifra, { rejectWithValue }) => {
    // eslint-disable-next-line no-console
    console.warn("Deprecated, migrate to LookupTable hooks and components");

    try {
      await ApiClient.delete(`${baseUrl}/${sifra}`);
      return sifra;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const speciesCodeListSlice = createSlice({
  name: "speciesCodeLists",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // ---- LIST (ALL) ----
    builder.addCase(fetchSpeciesCodeList.pending, (state) => {
      state.isListLoading = true;
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchSpeciesCodeList.fulfilled, (state, action) => {
      state.list = action.payload;
      for (const item of action.payload) {
        if (item?.sifra) {
          state.byId[item.sifra] = item;
        }
      }
      state.isListLoading = false;
      state.isLoading = false;
    });
    builder.addCase(fetchSpeciesCodeList.rejected, (state, action) => {
      state.error = action.payload;
      state.isListLoading = false;
      state.isLoading = false;
    });

    // ---- SINGLE BY SIFRA ----
    builder.addCase(fetchSpeciesCodeBySifra.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchSpeciesCodeBySifra.fulfilled, (state, action) => {
      state.isLoading = false;
      const item = action.payload;
      if (state.byId === null) {
        state.byId = {};
      }
      if (item?.sifra) {
        state.byId[item.sifra] = item;
        state.details = item.sifra;
      }
    });
    builder.addCase(fetchSpeciesCodeBySifra.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });

    // ---- PAGE ----
    builder.addCase(fetchSpeciesCodePage.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchSpeciesCodePage.fulfilled, (state, action) => {
      state.isLoading = false;
      state.page = action.payload;
      const content = action.payload?.content || [];
      if (state.byId === null) {
        state.byId = {};
      }
      for (const item of content) {
        if (item?.sifra) {
          state.byId[item.sifra] = item;
        }
      }
    });
    builder.addCase(fetchSpeciesCodePage.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });

    // ---- CREATE ----
    builder.addCase(createSpeciesCode.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(createSpeciesCode.fulfilled, (state, action) => {
      state.isLoading = false;
      const item = action.payload;
      if (!state.byId) {
        state.byId = {};
      }
      if (item?.sifra) {
        state.byId[item.sifra] = item;
        state.list.push(item);
        state.details = item.sifra;
      }
    });
    builder.addCase(createSpeciesCode.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });

    // ---- UPDATE ----
    builder.addCase(updateSpeciesCode.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(updateSpeciesCode.fulfilled, (state, action) => {
      state.isLoading = false;
      const item = action.payload;
      if (item?.sifra) {
        state.byId[item.sifra] = item;
        const idx = state.list.findIndex((s) => s.sifra === item.sifra);
        if (idx !== -1) {
          state.list[idx] = item;
        }
        state.details = item.sifra;
      }
    });
    builder.addCase(updateSpeciesCode.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });

    // ---- DELETE ----
    builder.addCase(deleteSpeciesCode.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(deleteSpeciesCode.fulfilled, (state, action) => {
      state.isLoading = false;
    });
    builder.addCase(deleteSpeciesCode.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });
  },
});

export default speciesCodeListSlice.reducer;

/**
 * Example response item shape (for reference):
 *
 * {
 *   "sifra": "OVCA",
 *   "naziv": {
 *     "key": "SPECIES_OVCA_NAZIV",
 *     "sl": "Ovca",
 *     "en": "Sheep",
 *     "version": 1,
 *     "createdOn": "2025-12-10T15:53:33.473Z",
 *     "createdBy": "00000000-0000-0000-0000-000000000000",
 *     "updatedOn": null,
 *     "updatedBy": null
 *   },
 *   "opis": {
 *     "key": "SPECIES_OVCA_OPIS",
 *     "sl": "Opis vrste ovca",
 *     "en": "Sheep species description",
 *     "version": 1,
 *     "createdOn": "2025-12-10T15:53:33.473Z",
 *     "createdBy": "00000000-0000-0000-0000-000000000000",
 *     "updatedOn": null,
 *     "updatedBy": null
 *   },
 *   "veljaZaKoze": true,
 *   "veljaZaOvce": true,
 *   "veljaZaKonje": true,
 *   "veljaZaGovedo": true,
 *   "veljaZaPrasice": true,
 *   "veljaZaSamce": true,
 *   "veljaZaSamice": true,
 *   "createdOn": "2025-12-10T15:53:33.473Z",
 *   "createdBy": null,
 *   "updatedOn": null,
 *   "updatedBy": null,
 *   "version": 1
 * }
 */

export const SHEEP_GOAT_SPECIES_CODE_LIST = createSelector(
  (state) => state.speciesCodeList,
  ({ list }) => list.filter((item) => item?.veljaZaKoze && item?.veljaZaOvce)
);

export const DROBNICA_SPECIES_CODE_LIST = createSelector(
  (state) => state.speciesCodeList,
  ({ list }) =>
    (list ?? []).filter(
      (x) => x?.sifra === SPECIES.GOAT.id || x?.sifra === SPECIES.SHEEP.id
    )
);
